// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MenuItem } from '@project/service/menu/menu.interfaces';
import { AlertHelper } from '@shared/alert/alert.helper';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TabsetComponent } from 'ngx-bootstrap/tabs';
import { ArticleEndPlateInfoVO } from '@article/model/vo/article-end-plate-info-vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleDataService } from '@article/service/article-data.service';
import { AdminToolService } from '@admintool/services/admintool.service';
import { AdmintoolComponentsPresenter } from '../../../presenter/admintool-components.presenter';
import { SharedComponentsPresenter } from '@shared/shared-components.presenter';

@Component({
  standalone: false,
  selector: 'app-manage-end-plates',
  templateUrl: './manage-end-plates.component.html',
  styleUrls: ['./manage-end-plates.component.scss'],
  providers: [TabsetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageEndPlatesComponent implements OnInit {
  public endPlateList: ArticleEndPlateInfoVO[];
  public newOrUpdatedEndPlates = new Map<String, ArticleEndPlateInfoVO>();

  public articleNo: string;

  constructor(
    private bsModalRef: BsModalRef,
    private cdRef: ChangeDetectorRef,
    private alertHelper: AlertHelper,
    private adminToolService: AdminToolService,
    private articleDataService: ArticleDataService,
    private admintoolComponentsPresenter: AdmintoolComponentsPresenter,
    private sharedComponentsPresenter: SharedComponentsPresenter,
  ) {}

  ngOnInit(): void {
    this.adminToolService.getArticleEndPlateInfo(this.articleNo).subscribe((endPlates: ArticleEndPlateInfoVO[]) => {
      this.endPlateList = endPlates;
      this.cdRef.markForCheck();
    });
  }

  public getHeaderMenu(): MenuItem[] {
    const items: MenuItem[] = [];
    items.push({
      label: 'global.label.add',
      action: () => this.openAddEndPlateDialog(),
      icon: 'plus',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'global.label.delete_all',
      action: () => this.deleteAll(),
      icon: 'delete',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'manageAccessories.header.copyAccessoriesFrom',
      action: () => this.openCopyEndPlateFromDialog(),
      icon: 'copy',
      enabled: true,
      visible: true,
    });

    return items;
  }

  public getEndPlateMenu(endPlate: ArticleEndPlateInfoVO): MenuItem[] {
    const items: MenuItem[] = [];
    items.push({
      label: 'global.label.edit',
      action: () => this.openEditEndPlateInfoDialog(endPlate),
      icon: 'edit-pen',
      enabled: true,
      visible: true,
    });
    items.push({
      label: 'global.label.delete',
      action: () => this.deleteEndPlateInfo(endPlate),
      icon: 'delete',
      enabled: true,
      visible: true,
    });

    return items;
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): void {
    this.adminToolService.saveEndPlateEntries(Array.from(this.newOrUpdatedEndPlates.values())).subscribe(() => this.close());
  }

  private deleteEndPlateInfo(endPlate: ArticleEndPlateInfoVO): void {
    const index: number = this.endPlateList.indexOf(endPlate);
    this.endPlateList[index].deleted = 1;
    this.newOrUpdatedEndPlates.set(this.endPlateList[index].otherArticleNo, this.endPlateList[index]);
  }

  private async openEditEndPlateInfoDialog(endPlate: ArticleEndPlateInfoVO): Promise<void> {
    const rotated = await this.admintoolComponentsPresenter.openEditEndPlateInfoPopup(endPlate);
    const currentEndPlate = this.endPlateList.find((element: ArticleEndPlateInfoVO) => {
      return element.otherArticleNo === endPlate.otherArticleNo;
    });
    currentEndPlate.rotated = rotated;
    this.newOrUpdatedEndPlates.set(currentEndPlate.otherArticleNo, currentEndPlate);
    this.cdRef.markForCheck();
  }

  private openCopyEndPlateFromDialog(): void {
    this.sharedComponentsPresenter.openCopyFromPopup('endplate').then((endPlateArticleSource: string) => {
      this.articleDataService.loadArticleByIdOrArticleNumberAndVersion(endPlateArticleSource).subscribe((article: ArticleVO) => {
        if (article) {
          this.adminToolService.getArticleEndPlateInfo(article.articleNo).subscribe((endPlateInfos: ArticleEndPlateInfoVO[]) => {
            if (endPlateInfos) {
              endPlateInfos.map((endPlateInfo: ArticleEndPlateInfoVO) => {
                const existingEndPlate: ArticleEndPlateInfoVO = this.endPlateList.find(
                  (item: ArticleEndPlateInfoVO) => item.otherArticleNo === endPlateInfo.otherArticleNo
                );
                if (!existingEndPlate) {
                  endPlateInfo.articleNo = this.articleNo;
                  endPlateInfo.id = null;
                  this.endPlateList.push(endPlateInfo);
                  this.newOrUpdatedEndPlates.set(endPlateInfo.otherArticleNo, endPlateInfo);
                } else if (existingEndPlate.deleted === 1) {
                  existingEndPlate.deleted = 0;
                  this.endPlateList = this.endPlateList.filter((endPlate) => {
                    return endPlate.otherArticleNo !== existingEndPlate.otherArticleNo;
                  });
                  this.endPlateList.push(existingEndPlate);
                  this.newOrUpdatedEndPlates.set(existingEndPlate.otherArticleNo, existingEndPlate);
                }
              });
              this.cdRef.markForCheck();
            } else {
              this.alertHelper.showError('manageEndPlates.error.title.noEndPlateInfo', 'manageEndPlates.error.message.noEndPlateInfo');
            }
          });
        } else {
          this.alertHelper.showError('searchArticleView.title.no_articles_found', 'searchArticleView.message.no_articles_found', {
            translationParams: [endPlateArticleSource],
          });
        }
      });
    });
  }

  private deleteAll(): void {
    this.endPlateList.forEach((endPlate) => {
      endPlate.deleted = 1;
      this.newOrUpdatedEndPlates.set(endPlate.otherArticleNo, endPlate);
    });
  }

  private async openAddEndPlateDialog(): Promise<void> {
    const endPlateInfo = await this.admintoolComponentsPresenter.openAddEndPlatePopup();
    const isNewEndPlate: boolean = !this.endPlateList.some((endPlate: ArticleEndPlateInfoVO) => {
      return endPlate.otherArticleNo === endPlateInfo.otherArticleNo;
    });
    if (isNewEndPlate) {
      endPlateInfo.articleNo = this.articleNo;
      this.endPlateList.push(endPlateInfo);
      this.newOrUpdatedEndPlates.set(endPlateInfo.otherArticleNo, endPlateInfo);
      this.cdRef.markForCheck();
    } else {
      await this.alertHelper.showError('manageEndPlates.error.title.endPlate_already_exists', 'manageEndPlates.error.message.endPlate_already_exists');
    }
  }
}
